/**
 * Communications API Service
 *
 * Cliente API para el backend de comunicaciones
 * Reemplaza el mock data actual con llamadas reales al backend Django
 */

import { apiClient } from '@/lib/api-client';

// ============================================================================
// TYPES (alineados con backend Django)
// ============================================================================

export interface ConversationChannel {
  id: string;
  company: string;
  project: string | null;
  channel_type: 'whatsapp_cloud' | 'facebook_messenger' | 'instagram_dm' | 'email_smtp' | 'webchat';
  channel_name: string;
  channel_identifier: string;
  platform_name: string;
  is_active: boolean;
  is_verified: boolean;
  is_configured: boolean;
  verified_at: string | null;
  verification_status: 'pending' | 'verified' | 'failed' | 'expired';
  ai_enabled: boolean;
  ai_model: string;
  auto_reply_enabled: boolean;
  auto_assign_enabled: boolean;
  total_messages_sent: number;
  total_messages_received: number;
  total_conversations: number;
  last_message_at: string | null;
  created_at: string;
}

export interface Conversation {
  id: string;
  conversation_id: string;
  lead: string;
  lead_name: string;
  lead_email: string;
  lead_phone: string;
  lead_score: number;
  channel: string;
  channel_name: string;
  channel_platform: string;
  channel_type: string;
  status: 'active' | 'bot_handling' | 'agent_handling' | 'waiting_lead_reply' | 'waiting_agent_reply' | 'resolved' | 'archived';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_at: string | null;
  bot_active: boolean;
  bot_escalated: boolean;
  message_count: number;
  lead_message_count: number;
  last_message_at: string;
  has_unread_messages: boolean;
  is_starred: boolean;
  requires_followup: boolean;
  tags: string[];
  key_topics: string[];
  overall_sentiment: number | null;
  quality_score: number;
  created_at: string;
}

export interface Message {
  id: string;
  conversation: string;
  lead: string;
  direction: 'inbound' | 'outbound';
  sender_type: 'lead' | 'bot' | 'agent' | 'system';
  sender_name: string;
  message_type: 'text' | 'image' | 'audio' | 'video' | 'document' | 'template' | 'interactive';
  content: string;
  media_url: string;
  media_filename: string;
  status: 'pending' | 'queued' | 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  failure_reason: string;
  sent_at: string;
  delivered_at: string | null;
  read_at: string | null;
  is_from_lead: boolean;
  is_from_bot: boolean;
  is_from_agent: boolean;
  has_media: boolean;
  ai_intent: string;
  ai_sentiment: number | null;
  ai_entities: Record<string, any>;
  channel_type: string | null; // e.g. 'whatsapp_cloud', 'instagram_dm', etc.
  metadata?: Record<string, any>; // reactions, whatsapp_message_id, etc.
  created_at: string;
}

export interface SendMessagePayload {
  text?: string;
  message_type?: 'text' | 'image' | 'document' | 'audio' | 'video' | 'template';
  media_url?: string;
  media_filename?: string;
  template_name?: string;
  template_language?: string;
  template_params?: string[];
  quick_reply_id?: string;
  reply_to_message_id?: string;
  metadata?: Record<string, any>;
  /** Send message as another user (impersonation). Platform/manager users only. */
  on_behalf_of?: string;
}

// ============================================================================
// ML INTELLIGENCE TYPES
// ============================================================================

export interface ConversationMLInsight {
  id: string;
  conversation: string;
  lead: string;
  triggering_message: string | null;
  insight_type: 'buying_signal' | 'objection' | 'sentiment_shift' | 'churn_risk' | 'engagement_drop' | 'hot_lead' | 'information_request' | 'price_concern' | 'competitor_mention' | 'decision_maker';
  insight_type_display: string;
  category: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  severity_display: string;
  title: string;
  description: string;
  confidence_score: number;
  evidence: {
    keywords?: string[];
    patterns?: string[];
    context?: Record<string, any>;
  };
  predicted_conversion_probability: number | null;
  predicted_close_date: string | null;
  predicted_lead_value: number | null;
  suggested_action: string;
  suggested_response: string;
  priority: number;
  status: 'new' | 'reviewed' | 'acted_upon' | 'dismissed';
  status_display: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  acted_upon_at: string | null;
  semantic_id: string;
  created_at: string;
  updated_at: string;
  // Related details
  conversation_detail?: {
    id: string;
    status: string;
    channel_name: string | null;
  };
  lead_detail?: {
    id: string;
    full_name: string;
    email: string;
    phone: string;
  };
  triggering_message_preview?: {
    id: string;
    content_preview: string;
    sent_at: string;
  };
  reviewed_by_detail?: {
    id: string;
    full_name: string;
    email: string;
  };
}

export interface MessageBehaviorPattern {
  id: string;
  message: string;
  conversation: string;
  lead: string;
  intent: 'greeting' | 'question' | 'objection' | 'interest' | 'appointment_request' | 'price_inquiry' | 'information_request' | 'complaint' | 'positive_feedback' | 'negotiation' | 'closing_intent' | 'referral' | 'other';
  intent_display: string;
  intent_confidence: number;
  sentiment: 'very_negative' | 'negative' | 'neutral' | 'positive' | 'very_positive';
  sentiment_display: string;
  sentiment_score: number;
  entities: {
    persons?: string[];
    locations?: string[];
    dates?: string[];
    prices?: string[];
    projects?: string[];
    competitors?: string[];
  };
  keywords: string[];
  buying_signals: Array<{
    signal: string;
    evidence: string;
    strength: number;
  }>;
  objections: Array<{
    type: string;
    objection: string;
    severity: string;
  }>;
  questions: string[];
  lead_score_delta: number;
  trigger_score_recalculation: boolean;
  model_version: string;
  processed_at: string;
  created_at: string;
  updated_at: string;
  // Related details
  message_preview?: {
    id: string;
    content: string;
    direction: string;
    sent_at: string;
  };
  conversation_detail?: {
    id: string;
    status: string;
  };
  has_buying_signals?: boolean;
  has_objections?: boolean;
}

export interface ChannelMLPerformance {
  id: string;
  channel: string;
  project: string | null;
  period_start: string;
  period_end: string;
  period_type: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  period_type_display: string;
  total_conversations: number;
  active_conversations: number;
  avg_messages_per_conversation: number;
  avg_conversation_duration_seconds: number;
  avg_first_response_seconds: number;
  avg_agent_response_seconds: number;
  leads_generated: number;
  leads_converted: number;
  conversion_rate: number;
  avg_lead_score: number;
  total_revenue: number;
  avg_sentiment_score: number;
  positive_sentiment_rate: number;
  engagement_score: number;
  total_buying_signals: number;
  buying_signal_rate: number;
  total_objections: number;
  objection_rate: number;
  objection_categories: Record<string, number>;
  performance_grade: 'A' | 'B' | 'C' | 'D' | 'F';
  performance_grade_display: string;
  insights: Array<{
    type: string;
    title: string;
    recommendation: string;
  }>;
  ranking_position: number | null;
  percentile: number | null;
  calculated_at: string;
  ml_model_version: string;
  created_at: string;
  updated_at: string;
  // Related details
  channel_detail?: {
    id: string;
    channel_name: string;
    channel_type: string;
    platform_name: string;
  };
  project_detail?: {
    id: string;
    name: string;
  };
  roi_estimate?: number;
  performance_summary?: {
    grade: string;
    conversion_rate: number;
    avg_lead_score: number;
    engagement_score: number;
    total_revenue: number;
  };
}

// ============================================================================
// CHANNELS API
// ============================================================================

export const channelsApi = {
  /**
   * Listar canales
   */
  async list(params?: {
    channel_type?: string;
    is_active?: boolean;
    project?: string;
    company?: string;
  }): Promise<ConversationChannel[]> {
    return await apiClient.get<ConversationChannel[]>('/communications/channels/', { params });
  },

  /**
   * Obtener canal por ID
   */
  async get(id: string): Promise<ConversationChannel> {
    return await apiClient.get<ConversationChannel>(`/communications/channels/${id}/`);
  },

  /**
   * Crear canal
   */
  async create(data: Partial<ConversationChannel>): Promise<ConversationChannel> {
    return await apiClient.post<ConversationChannel>('/communications/channels/', data);
  },

  /**
   * Actualizar canal
   */
  async update(id: string, data: Partial<ConversationChannel>): Promise<ConversationChannel> {
    return await apiClient.patch<ConversationChannel>(`/communications/channels/${id}/`, data);
  },

  /**
   * Eliminar canal
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/communications/channels/${id}/`);
  },

  /**
   * Verificar canal con plataforma externa
   */
  async verify(id: string): Promise<{ status: string; message: string; verified_at?: string }> {
    return await apiClient.post<{ status: string; message: string; verified_at?: string }>(`/communications/channels/${id}/verify/`);
  },

  /**
   * Enviar mensaje de prueba
   */
  async test(id: string, data: { to: string; message?: string }): Promise<{ status: string; message: string }> {
    return await apiClient.post<{ status: string; message: string }>(`/communications/channels/${id}/test/`, data);
  },

  /**
   * Obtener estadísticas del canal
   */
  async stats(id: string): Promise<{
    total_conversations: number;
    active_conversations: number;
    total_messages: number;
    messages_sent: number;
    messages_received: number;
    avg_messages_per_conversation: number;
  }> {
    return await apiClient.get<{
      total_conversations: number;
      active_conversations: number;
      total_messages: number;
      messages_sent: number;
      messages_received: number;
      avg_messages_per_conversation: number;
    }>(`/communications/channels/${id}/stats/`);
  },
};

// ============================================================================
// CONVERSATIONS API
// ============================================================================

export const conversationsApi = {
  /**
   * Listar conversaciones
   */
  async list(params?: {
    status?: string;
    priority?: string;
    assigned_to?: string;
    lead?: string;
    channel?: string;
    company?: string;
    project?: string;
    conversation_type?: 'client' | 'team';
    has_unread_messages?: boolean;
    needs_attention?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{ count: number; results: Conversation[] }> {
    return await apiClient.get<{ count: number; results: Conversation[] }>('/communications/conversations/', { params });
  },

  /**
   * Obtener conversación por ID
   */
  async get(id: string): Promise<Conversation> {
    return await apiClient.get<Conversation>(`/communications/conversations/${id}/`);
  },

  /**
   * Crear conversación
   */
  async create(data: {
    lead: string;
    channel: string;
    external_conversation_id?: string;
    priority?: string;
  }): Promise<Conversation> {
    return await apiClient.post<Conversation>('/communications/conversations/', data);
  },

  /**
   * Crear conversación de equipo (team chat)
   */
  async createTeamConversation(participants: string[], title?: string, company?: string): Promise<Conversation> {
    return await apiClient.post<Conversation>('/communications/conversations/team/', {
      participants,
      title,
      ...(company ? { company } : {}),
    });
  },

  /**
   * Actualizar conversación
   */
  async update(id: string, data: Partial<Conversation>): Promise<Conversation> {
    return await apiClient.patch<Conversation>(`/communications/conversations/${id}/`, data);
  },

  /**
   * Obtener mensajes de la conversación
   */
  async getMessages(
    conversationId: string,
    params?: {
      limit?: number;
      offset?: number;
      before?: string;
      after?: string;
    }
  ): Promise<{ count: number; results: Message[] }> {
    return await apiClient.get<{ count: number; results: Message[] }>(`/communications/conversations/${conversationId}/messages/`, { params });
  },

  /**
   * Enviar mensaje
   */
  async sendMessage(conversationId: string, payload: SendMessagePayload): Promise<Message> {
    return await apiClient.post<Message>(
      `/communications/conversations/${conversationId}/send_message/`,
      payload
    );
  },

  /**
   * Upload file attachment for a conversation
   */
  async uploadAttachment(conversationId: string, file: File): Promise<{
    url: string;
    filename: string;
    mime_type: string;
    size: number;
    message_type: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    return await apiClient.post<{
      url: string;
      filename: string;
      mime_type: string;
      size: number;
      message_type: string;
    }>(`/communications/conversations/${conversationId}/upload_attachment/`, formData);
  },

  /**
   * Marcar como leída
   */
  async markAsRead(conversationId: string): Promise<{ status: string; message: string }> {
    return await apiClient.post<{ status: string; message: string }>(`/communications/conversations/${conversationId}/mark_as_read/`);
  },

  /**
   * Asignar a agente
   */
  async assign(conversationId: string, userId: string, method?: string): Promise<{
    status: string;
    message: string;
    assigned_to: { id: string; name: string };
  }> {
    return await apiClient.post<{
      status: string;
      message: string;
      assigned_to: { id: string; name: string };
    }>(`/communications/conversations/${conversationId}/assign/`, {
      user_id: userId,
      method,
    });
  },

  /**
   * Escalar a humano
   */
  async escalate(conversationId: string, reason: string): Promise<{ status: string; message: string; reason: string }> {
    return await apiClient.post<{ status: string; message: string; reason: string }>(`/communications/conversations/${conversationId}/escalate/`, { reason });
  },

  /**
   * Activar/desactivar bot
   */
  async toggleBot(conversationId: string, enabled: boolean, reason?: string): Promise<{ status: string; bot_active: boolean }> {
    return await apiClient.post<{ status: string; bot_active: boolean }>(`/communications/conversations/${conversationId}/bot_toggle/`, {
      enabled,
      reason,
    });
  },

  /**
   * Agregar tag
   */
  async addTag(conversationId: string, tag: string): Promise<{ status: string; tags: string[] }> {
    return await apiClient.post<{ status: string; tags: string[] }>(`/communications/conversations/${conversationId}/add_tag/`, { tag });
  },

  /**
   * Quitar tag
   */
  async removeTag(conversationId: string, tag: string): Promise<{ status: string; tags: string[] }> {
    return await apiClient.post<{ status: string; tags: string[] }>(`/communications/conversations/${conversationId}/remove_tag/`, { tag });
  },

  /**
   * Enviar plantilla WhatsApp vía Meta Cloud API
   */
  async sendTemplate(
    conversationId: string,
    templateName: string,
    templateData?: Record<string, any>
  ): Promise<{
    success: boolean;
    message_id?: string;
    template_name?: string;
    message_record?: any;
    error?: string;
  }> {
    return await apiClient.post<{
      success: boolean;
      message_id?: string;
      template_name?: string;
      message_record?: any;
      error?: string;
    }>(`/communications/conversations/${conversationId}/send_template/`, {
      template_name: templateName,
      template_data: templateData,
    });
  },
};

// ============================================================================
// MESSAGES API
// ============================================================================

export const messagesApi = {
  /**
   * Listar mensajes
   */
  async list(params?: {
    conversation?: string;
    lead?: string;
    direction?: 'inbound' | 'outbound';
    sender_type?: string;
    ai_intent?: string;
    ordering?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ count: number; results: Message[] }> {
    return await apiClient.get<{ count: number; results: Message[] }>('/communications/messages/', { params });
  },

  /**
   * Obtener mensaje por ID
   */
  async get(id: string): Promise<Message> {
    return await apiClient.get<Message>(`/communications/messages/${id}/`);
  },

  /**
   * Marcar como entregado
   */
  async markAsDelivered(id: string): Promise<{ status: string; delivered_at: string }> {
    return await apiClient.post<{ status: string; delivered_at: string }>(`/communications/messages/${id}/mark_as_delivered/`);
  },

  /**
   * Marcar como leído
   */
  async markAsRead(id: string): Promise<{ status: string; read_at: string }> {
    return await apiClient.post<{ status: string; read_at: string }>(`/communications/messages/${id}/mark_as_read/`);
  },

  /**
   * Reintentar envío de mensaje fallido
   */
  async retry(id: string): Promise<{ status: string }> {
    return await apiClient.post<{ status: string }>(`/communications/messages/${id}/retry/`);
  },

  /**
   * Get messages for a lead across all conversations (grouped view).
   * Uses proper limit/offset pagination, returns messages in chronological order.
   */
  async byLead(leadId: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<{ count: number; results: Message[]; has_more: boolean }> {
    return await apiClient.get<{ count: number; results: Message[]; has_more: boolean }>(
      `/communications/messages/by-lead/${leadId}/`,
      { params }
    );
  },
};

/**
 * React to a message in a conversation
 */
export async function reactToMessage(
  conversationId: string,
  messageId: string,
  emoji: string
): Promise<{ status: string; message_id: string; reactions: Array<{ emoji: string; sender_type: string; timestamp: string }> }> {
  return await apiClient.post<{
    status: string;
    message_id: string;
    reactions: Array<{ emoji: string; sender_type: string; timestamp: string }>;
  }>(`/communications/conversations/${conversationId}/react_to_message/`, {
    message_id: messageId,
    emoji,
  });
}

// ============================================================================
// ML INSIGHTS API
// ============================================================================

export const mlInsightsApi = {
  /**
   * Listar insights ML
   */
  async list(params?: {
    insight_type?: string;
    severity?: string;
    status?: string;
    conversation?: string;
    lead?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ count: number; results: ConversationMLInsight[] }> {
    return await apiClient.get<{ count: number; results: ConversationMLInsight[] }>('/communications/ml-insights/', { params });
  },

  /**
   * Obtener insight por ID
   */
  async get(id: string): Promise<ConversationMLInsight> {
    return await apiClient.get<ConversationMLInsight>(`/communications/ml-insights/${id}/`);
  },

  /**
   * Marcar insight como revisado
   */
  async markAsReviewed(id: string): Promise<{ status: string; reviewed_at: string }> {
    return await apiClient.post<{ status: string; reviewed_at: string }>(`/communications/ml-insights/${id}/mark_as_reviewed/`);
  },

  /**
   * Marcar insight como aplicado
   */
  async markAsActedUpon(id: string): Promise<{ status: string; acted_upon_at: string }> {
    return await apiClient.post<{ status: string; acted_upon_at: string }>(`/communications/ml-insights/${id}/mark_as_acted_upon/`);
  },

  /**
   * Descartar insight
   */
  async dismiss(id: string): Promise<{ status: string; dismissed_at: string }> {
    return await apiClient.post<{ status: string; dismissed_at: string }>(`/communications/ml-insights/${id}/dismiss/`);
  },

  /**
   * Revisar múltiples insights
   */
  async bulkReview(insightIds: string[], action: 'reviewed' | 'acted_upon' | 'dismissed'): Promise<{ status: string; updated_count: number }> {
    return await apiClient.post<{ status: string; updated_count: number }>('/communications/ml-insights/bulk_review/', {
      insight_ids: insightIds,
      action,
    });
  },
};

// ============================================================================
// MESSAGE PATTERNS API
// ============================================================================

export const messagePatternsApi = {
  /**
   * Listar patrones de mensajes
   */
  async list(params?: {
    intent?: string;
    sentiment?: string;
    conversation?: string;
    lead?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ count: number; results: MessageBehaviorPattern[] }> {
    return await apiClient.get<{ count: number; results: MessageBehaviorPattern[] }>('/communications/message-patterns/', { params });
  },

  /**
   * Obtener patrón por ID
   */
  async get(id: string): Promise<MessageBehaviorPattern> {
    return await apiClient.get<MessageBehaviorPattern>(`/communications/message-patterns/${id}/`);
  },

  /**
   * Obtener mensajes con buying signals
   */
  async getBuyingSignals(params?: { limit?: number }): Promise<MessageBehaviorPattern[]> {
    return await apiClient.get<MessageBehaviorPattern[]>('/communications/message-patterns/buying_signals/', { params });
  },

  /**
   * Obtener mensajes con objeciones
   */
  async getObjections(params?: { limit?: number }): Promise<MessageBehaviorPattern[]> {
    return await apiClient.get<MessageBehaviorPattern[]>('/communications/message-patterns/objections/', { params });
  },
};

// ============================================================================
// CHANNEL PERFORMANCE API
// ============================================================================

export const channelPerformanceApi = {
  /**
   * Listar performance de canales
   */
  async list(params?: {
    channel?: string;
    project?: string;
    period_type?: 'daily' | 'weekly' | 'monthly' | 'quarterly';
    performance_grade?: 'A' | 'B' | 'C' | 'D' | 'F';
    limit?: number;
    offset?: number;
  }): Promise<{ count: number; results: ChannelMLPerformance[] }> {
    return await apiClient.get<{ count: number; results: ChannelMLPerformance[] }>('/communications/channel-performance/', { params });
  },

  /**
   * Obtener performance por ID
   */
  async get(id: string): Promise<ChannelMLPerformance> {
    return await apiClient.get<ChannelMLPerformance>(`/communications/channel-performance/${id}/`);
  },

  /**
   * Obtener ranking de canales
   */
  async getRanking(params?: {
    period_type?: 'daily' | 'weekly' | 'monthly' | 'quarterly';
    metric?: 'conversion_rate' | 'total_revenue' | 'engagement_score';
  }): Promise<ChannelMLPerformance[]> {
    return await apiClient.get<ChannelMLPerformance[]>('/communications/channel-performance/ranking/', { params });
  },

  /**
   * Obtener tendencias de un canal
   */
  async getTrends(channelId: string, periodType?: 'daily' | 'weekly' | 'monthly' | 'quarterly'): Promise<ChannelMLPerformance[]> {
    return await apiClient.get<ChannelMLPerformance[]>('/communications/channel-performance/trends/', {
      params: {
        channel: channelId,
        period_type: periodType || 'monthly',
      },
    });
  },

  /**
   * Comparar dos canales
   */
  async compare(channel1Id: string, channel2Id: string, periodType?: 'daily' | 'weekly' | 'monthly' | 'quarterly'): Promise<{
    channel1: ChannelMLPerformance;
    channel2: ChannelMLPerformance;
    comparison: {
      conversion_rate_diff: number;
      revenue_diff: number;
      lead_score_diff: number;
      engagement_diff: number;
    };
  }> {
    return await apiClient.get<{
      channel1: ChannelMLPerformance;
      channel2: ChannelMLPerformance;
      comparison: {
        conversion_rate_diff: number;
        revenue_diff: number;
        lead_score_diff: number;
        engagement_diff: number;
      };
    }>('/communications/channel-performance/compare/', {
      params: {
        channel1: channel1Id,
        channel2: channel2Id,
        period_type: periodType || 'monthly',
      },
    });
  },
};

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

export default {
  channels: channelsApi,
  conversations: conversationsApi,
  messages: messagesApi,
  mlInsights: mlInsightsApi,
  messagePatterns: messagePatternsApi,
  channelPerformance: channelPerformanceApi,
};
